/**
 * VoiceRecorder — one short voice clip via MediaRecorder, with a live input
 * level and silence / no-speech detection. Plain TypeScript, no React.
 * Plan: docs/plans/voice-edit-ui-plan.md section 7.
 *
 * Lifecycle: new VoiceRecorder(limits, callbacks) -> start() -> (stop() | auto
 * stop) -> exactly one of onClip / onFailure -> dispose(). cancel() discards
 * without calling either. Every method is safe to call more than once.
 */
import { VoiceClip, VoiceLimits } from "../../interfaces/VoiceObject";

/** Tuning knobs, kept together so they can be adjusted in one place. */
export const VOICE_RECORDER_TUNING = {
  /** RMS above this for speechHoldMs counts as "speech has started". */
  speechRms: 0.04,
  speechHoldMs: 150,
  /** After speech, RMS below this for silenceAfterSpeechMs auto-stops. */
  silenceRms: 0.02,
  silenceAfterSpeechMs: 2000,
  /** No speech at all within this window: stop with NO_SPEECH. */
  noSpeechTimeoutMs: 8000,
  /** Smaller blobs are treated as too short. */
  minBlobBytes: 2048,
  audioBitsPerSecond: 32000,
  fftSize: 1024,
  /** RMS that maps to a full level ring in the UI (level = rms / this, capped at 1). */
  fullLevelRms: 0.25,
  /** MediaRecorder timeslice, so a long clip is not one giant buffer. */
  timesliceMs: 1000,
};

/** Preference order; the first one the browser supports AND the server accepts wins. */
const MIME_PREFERENCE = [
  "audio/webm;codecs=opus",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/webm",
];

export type VoiceRecorderFailure =
  | "NO_SPEECH"
  | "TOO_SHORT"
  | "TOO_LARGE"
  | "INTERRUPTED"
  | "MIC_DENIED"
  | "MIC_UNAVAILABLE"
  | "UNSUPPORTED";

/** How long to wait for MediaRecorder.onstop before finishing anyway. */
const STOP_GUARD_MS = 1500;

export type VoiceRecorderStopReason = "manual" | "silence" | "maxDuration";

export interface VoiceRecorderCallbacks {
  /** Mic granted and recording started. */
  onStarted: (startedAt: number) => void;
  /** 0..1, once per animation frame while recording. */
  onLevel?: (level: number) => void;
  /** A usable clip. */
  onClip: (clip: VoiceClip, reason: VoiceRecorderStopReason) => void;
  /** Nothing usable. */
  onFailure: (failure: VoiceRecorderFailure) => void;
}

type AudioContextCtor = typeof AudioContext;

function audioContextCtor(): AudioContextCtor | undefined {
  if (typeof window === "undefined") return undefined;
  return (window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext) as
    | AudioContextCtor
    | undefined;
}

/** True when this browser/WebView can record audio at all. */
export function isVoiceRecordingSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext === true &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof MediaRecorder !== "undefined"
  );
}

/** First preferred MIME type the browser can record whose base type the server accepts. */
export function pickRecorderMimeType(acceptedMimeTypes: string[]): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  const accepted = new Set(acceptedMimeTypes.map((m) => m.split(";")[0].trim().toLowerCase()));
  for (const candidate of MIME_PREFERENCE) {
    const base = candidate.split(";")[0];
    try {
      if (accepted.has(base) && MediaRecorder.isTypeSupported(candidate)) return candidate;
    } catch {
      // isTypeSupported can throw on some older WebViews -- try the next one
    }
  }
  return null;
}

export class VoiceRecorder {
  private readonly limits: VoiceLimits;
  private readonly callbacks: VoiceRecorderCallbacks;

  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private sampleBuffer: Float32Array | null = null;
  private chunks: Blob[] = [];
  private mimeType = "";

  private startedAt = 0;
  private speechAboveSince: number | null = null;
  private silenceSince: number | null = null;
  private speechStarted = false;
  /** False when the analyser can't run (suspended AudioContext): only manual stop and the cap apply. */
  private detectionEnabled = false;

  private rafId: number | null = null;
  private maxTimer: ReturnType<typeof setTimeout> | null = null;
  private stopGuard: ReturnType<typeof setTimeout> | null = null;

  private started = false;
  private finished = false;
  private disposed = false;
  /** Set when stop/auto-stop asked the MediaRecorder to stop; onstop then finalises. */
  private pendingStop: VoiceRecorderStopReason | null = null;
  /** Set when the recording must be discarded with this failure once the recorder stops. */
  private pendingFailure: VoiceRecorderFailure | null = null;

  constructor(limits: VoiceLimits, callbacks: VoiceRecorderCallbacks) {
    this.limits = limits;
    this.callbacks = callbacks;
  }

  /** Ask for the mic and start recording. Call from a user gesture (iOS needs it). */
  async start(): Promise<void> {
    if (this.started || this.disposed) return;
    this.started = true;

    if (!isVoiceRecordingSupported()) {
      this.fail("UNSUPPORTED");
      return;
    }
    const mimeType = pickRecorderMimeType(this.limits.acceptedMimeTypes);
    if (!mimeType) {
      this.fail("UNSUPPORTED");
      return;
    }
    this.mimeType = mimeType;

    // Create (and resume) the AudioContext synchronously, inside the user
    // gesture -- iOS keeps a context created after an await suspended.
    const Ctor = audioContextCtor();
    if (Ctor) {
      try {
        this.audioContext = new Ctor();
        void this.audioContext.resume().catch(() => undefined);
      } catch {
        this.audioContext = null;
      }
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (error) {
      const name = (error as { name?: string } | null)?.name;
      this.fail(name === "NotAllowedError" || name === "SecurityError" ? "MIC_DENIED" : "MIC_UNAVAILABLE");
      return;
    }
    if (this.disposed || this.finished) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    this.stream = stream;

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: VOICE_RECORDER_TUNING.audioBitsPerSecond,
      });
    } catch {
      this.fail("UNSUPPORTED");
      return;
    }
    this.recorder = recorder;
    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) this.chunks.push(event.data);
    };
    recorder.onerror = () => this.interrupt();
    recorder.onstop = () => this.finalise();

    stream.getTracks().forEach((track) => track.addEventListener("ended", this.handleTrackEnded));
    document.addEventListener("visibilitychange", this.handleVisibility);

    this.setUpLevelMeter(stream);

    try {
      recorder.start(VOICE_RECORDER_TUNING.timesliceMs);
    } catch {
      this.fail("UNSUPPORTED");
      return;
    }
    this.startedAt = Date.now();
    this.maxTimer = setTimeout(() => this.requestStop("maxDuration"), this.limits.maxDurationMs);
    this.callbacks.onStarted(this.startedAt);
    if (this.analyser) this.rafId = requestAnimationFrame(this.tick);
  }

  /** Manual stop: the clip is delivered via onClip (or onFailure when too short). */
  stop(): void {
    this.requestStop("manual");
  }

  /** Discard the recording; no callback fires. */
  cancel(): void {
    if (this.finished) return;
    this.finished = true;
    this.cleanup();
  }

  /** Release everything. Idempotent. */
  dispose(): void {
    this.disposed = true;
    if (!this.finished) this.finished = true;
    this.cleanup();
  }

  // ─── internals ───────────────────────────────────────────────────────────

  private setUpLevelMeter(stream: MediaStream): void {
    const context = this.audioContext;
    if (!context) return;
    try {
      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = VOICE_RECORDER_TUNING.fftSize;
      source.connect(analyser);
      this.analyser = analyser;
      this.sampleBuffer = new Float32Array(analyser.fftSize);
      // A suspended context reads silence: silence / no-speech detection would
      // then stop a real recording, so it only runs while the context runs.
      this.detectionEnabled = context.state === "running";
    } catch {
      this.analyser = null;
      this.detectionEnabled = false;
    }
  }

  private tick = (): void => {
    if (this.finished || !this.analyser || !this.sampleBuffer) return;
    const now = Date.now();
    this.analyser.getFloatTimeDomainData(this.sampleBuffer);
    let sum = 0;
    for (let i = 0; i < this.sampleBuffer.length; i += 1) {
      const sample = this.sampleBuffer[i];
      sum += sample * sample;
    }
    const rms = Math.sqrt(sum / this.sampleBuffer.length);
    this.callbacks.onLevel?.(Math.min(1, rms / VOICE_RECORDER_TUNING.fullLevelRms));

    if (!this.detectionEnabled && this.audioContext?.state === "running") {
      this.detectionEnabled = true;
    }

    if (this.detectionEnabled) {
      const tuning = VOICE_RECORDER_TUNING;
      if (!this.speechStarted) {
        if (rms > tuning.speechRms) {
          this.speechAboveSince ??= now;
          if (now - this.speechAboveSince >= tuning.speechHoldMs) this.speechStarted = true;
        } else {
          this.speechAboveSince = null;
        }
        if (!this.speechStarted && now - this.startedAt >= tuning.noSpeechTimeoutMs) {
          this.requestStop("manual", "NO_SPEECH");
          return;
        }
      } else if (rms < tuning.silenceRms) {
        this.silenceSince ??= now;
        if (now - this.silenceSince >= tuning.silenceAfterSpeechMs) {
          this.requestStop("silence");
          return;
        }
      } else {
        this.silenceSince = null;
      }
    }

    this.rafId = requestAnimationFrame(this.tick);
  };

  private requestStop(reason: VoiceRecorderStopReason, failure?: VoiceRecorderFailure): void {
    if (this.finished) return;
    // A second stop while the first is still pending (e.g. auto-stop fired, then the user
    // taps stop): never wait again, finish with what we have.
    if (this.pendingStop) {
      this.finalise();
      return;
    }
    this.pendingStop = reason;
    if (failure) this.pendingFailure = failure;
    this.stopLoops();
    const recorder = this.recorder;
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.stop(); // onstop -> finalise
        // Some browsers occasionally never fire onstop; don't leave the UI stuck recording.
        this.stopGuard = setTimeout(() => this.finalise(), STOP_GUARD_MS);
        return;
      } catch {
        // fall through and finalise with what we have
      }
    }
    this.finalise();
  }

  private finalise(): void {
    if (this.finished) return;
    this.finished = true;
    const durationMs = this.startedAt ? Date.now() - this.startedAt : 0;
    const type = this.recorder?.mimeType || this.mimeType;
    const blob = new Blob(this.chunks, { type });
    const reason = this.pendingStop ?? "manual";
    const failure = this.pendingFailure;
    this.cleanup();

    if (failure) {
      this.callbacks.onFailure(failure);
      return;
    }
    if (durationMs < this.limits.minDurationMs || blob.size < VOICE_RECORDER_TUNING.minBlobBytes) {
      this.callbacks.onFailure("TOO_SHORT");
      return;
    }
    if (blob.size > this.limits.maxAudioBytes) {
      this.callbacks.onFailure("TOO_LARGE");
      return;
    }
    this.callbacks.onClip({ blob, mimeType: type, durationMs }, reason);
  }

  private interrupt(): void {
    if (this.finished) return;
    this.finished = true;
    this.cleanup();
    this.callbacks.onFailure("INTERRUPTED");
  }

  private fail(failure: VoiceRecorderFailure): void {
    if (this.finished) return;
    this.finished = true;
    this.cleanup();
    if (!this.disposed) this.callbacks.onFailure(failure);
  }

  private handleTrackEnded = (): void => {
    this.interrupt();
  };

  private handleVisibility = (): void => {
    if (document.visibilityState === "hidden") this.interrupt();
  };

  private stopLoops(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.maxTimer !== null) {
      clearTimeout(this.maxTimer);
      this.maxTimer = null;
    }
  }

  private cleanup(): void {
    this.stopLoops();
    if (this.stopGuard !== null) {
      clearTimeout(this.stopGuard);
      this.stopGuard = null;
    }
    document.removeEventListener("visibilitychange", this.handleVisibility);
    const recorder = this.recorder;
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onerror = null;
      recorder.onstop = null;
      if (recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch {
          // already stopped
        }
      }
      this.recorder = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.removeEventListener("ended", this.handleTrackEnded);
        track.stop();
      });
      this.stream = null;
    }
    if (this.audioContext) {
      const context = this.audioContext;
      this.audioContext = null;
      if (context.state !== "closed") void context.close().catch(() => undefined);
    }
    this.analyser = null;
    this.sampleBuffer = null;
  }
}
